
import { Spinner } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { createContext, useContext, useEffect, useState } from "react";
import { BlockedUserState } from "../components/BlockedUserState";
import { InvalidUserState } from "../components/InvalidUserState";
import { NotAllowed } from "../components/NotAllowed";
import { userCanRead, userCanWrite } from "../services/AccessControl";
import { User } from "../types/user";
import { useAPI } from "./useAPI";

export const UserContext = createContext({} as any);

type ProviderProps = {
  children: JSX.Element
}

enum EnumUserStates {
  LOADING = 'loading',
  NOT_ALLOWED = 'not-allowed',
  INVALID = 'invalid',
  BLOCKED = 'blocked'
}

export function UserProvider({ children }: ProviderProps) {

  const { api } = useAPI();
  const [user, setUser] = useState<EnumUserStates | User>(EnumUserStates.LOADING);
  const router = useRouter();

  const { data: userData, error, mutate } = api.useMe();

  useEffect(() => {
    if (userData) {
      setUser(userData.data);
    } else if (error) {
      if (error.status === 403) {
        setUser(EnumUserStates.BLOCKED);
      } else {
        setUser(EnumUserStates.INVALID);
      }
    }
  }, [userData, error]);

  let canRead = false;
  let canWrite = false;
  if (typeof (user) !== 'string') {
    canRead = userCanRead(user, router.asPath);
    canWrite = userCanWrite(user, router.asPath);
  }

  const refreshMe = () => {
    mutate();
  }

  return (
    <UserContext.Provider
      value={{
        user,
        isEngysUser: typeof (user) !== 'string' ? user.account.name === 'ENGYS' : false,
        userCanRead: canRead,
        userCanWrite: canWrite,
        refreshMe: refreshMe
      }}
    >
      {user === EnumUserStates.LOADING && <Spinner style={{ position: 'fixed', top: '50%', left: '50%' }} size="lg" color="red" />}
      {user === EnumUserStates.INVALID && <InvalidUserState />}
      {user === EnumUserStates.BLOCKED && <BlockedUserState />}
      {(typeof (user) !== 'string') && (canRead ? children : <NotAllowed />)}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext);
  return context;
}